export interface CreateCachedFetchOptions {
  /** true 시 localStorage 사용 (탭/브라우저 종료 후에도 유지) */
  permanent?: boolean;
}

/**
 * 단건 조회 API용 캐시 래퍼
 *
 * - 결과 캐시: 동일 ID 재요청 시 즉시 반환
 * - 요청 중복 제거: 동시에 같은 ID로 여러 호출 시 1회만 fetch
 * - storageKeyPrefix 제공 시: storage에 영속 저장 → 새로고침 후에도 API 호출 방지
 * - permanent: true 시 localStorage 사용 (영구 캐시)
 *
 * @param fetcher - (id: string) => Promise<T> 형태의 raw API 함수
 * @param storageKeyPrefix - storage 키 접두사 (예: 'service-region')
 * @param options - permanent: true 시 localStorage 사용
 */
export function createCachedFetch<T>(
  fetcher: (id: string) => Promise<T>,
  storageKeyPrefix?: string,
  options?: CreateCachedFetchOptions
): (id: string) => Promise<T> {
  const cache = new Map<string, T>();
  const fetchPromises = new Map<string, Promise<T>>();
  const useLocalStorage = options?.permanent ?? false;

  const getStorageKey = (id: string) =>
    storageKeyPrefix ? `cached-fetch:${storageKeyPrefix}:${id}` : null;

  const getStorage = (): Storage | null =>
    typeof window !== 'undefined' ? (useLocalStorage ? localStorage : sessionStorage) : null;

  const getFromStorage = (id: string): T | undefined => {
    const storage = getStorage();
    if (!storage) return undefined;
    const key = getStorageKey(id);
    if (!key) return undefined;
    try {
      const raw = storage.getItem(key);
      if (!raw) return undefined;
      return JSON.parse(raw) as T;
    } catch {
      return undefined;
    }
  };

  const saveToStorage = (id: string, data: T) => {
    const storage = getStorage();
    if (!storage) return;
    const key = getStorageKey(id);
    if (!key) return;
    try {
      storage.setItem(key, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  };

  return async (id: string): Promise<T> => {
    const cached = cache.get(id);
    if (cached !== undefined) return cached;

    const fromStorage = getFromStorage(id);
    if (fromStorage !== undefined) {
      cache.set(id, fromStorage);
      return fromStorage;
    }

    let promise = fetchPromises.get(id);
    if (!promise) {
      promise = (async () => {
        try {
          const data = await fetcher(id);
          cache.set(id, data);
          saveToStorage(id, data);
          return data;
        } finally {
          fetchPromises.delete(id);
        }
      })();
      fetchPromises.set(id, promise);
    }
    return promise;
  };
}
